import type { ProjectDto, ProjectSection } from '@nsb/core';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useProject } from './hooks.ts';

/**
 * The pages of a project that sit in its Settings (Jason, 2026-10-07: "put people banners under settings ... Settings
 * should have that same paradigm that we see in the application level settings ... since we haven't set up billing yet,
 * why don't we just put that under the project level settings as well ... and the budget"), framed by
 * `ProjectSettingsFrame`. Each keeps its address, so every link into it, every email's button and the client's own way
 * round still land on it.
 */
const SETTINGS_PAGES = ['settings', 'people', 'budget', 'spent', 'banners'] as const;

/** Sub-pages carry one line back to the project; nothing more. */
export function ProjectCrumb({ project, children }: { project: ProjectDto | undefined; children?: ReactNode }) {
  return (
    <div className="mb-2 flex items-baseline gap-2">
      <Link to={`/p/${project?.id ?? ''}`} className="label hover:!text-ink">
        ← {project?.name ?? 'Project'}
      </Link>
      {children}
    </div>
  );
}

/**
 * NSB's sections of a project in words, grouped in the order the work reads: what happened, the design, the client's
 * decisions, the project itself. Which of them a job has is the API's to say, by the one rule (core `projectSections`,
 * sent as `ProjectDto.sections`). People, Budget, Billing and Banners are in its Settings (`ProjectSettingsFrame`);
 * the job's process is worked on its home, where a step opens to its panel, and set up in Settings (Jason, 2026-10-07:
 * "overview has the whole jobs process and phases, so I don't know why it would be also listed"); Client view gave way
 * to View as client.
 */
const GROUPS: [ProjectSection, string][][] = [
  [['overview', 'Overview']],
  [
    ['timeline', 'Timeline'],
    ['mail', 'Email'],
    ['files', 'Files'],
  ],
  [
    ['board', 'Design board'],
    ['patterns', 'Patterns'],
    ['reviews', 'Design reviews'],
  ],
  [['decisions', 'Decisions']],
  [['settings', 'Settings']],
];

/** The groups of the menu holding only the sections this job has; a group left empty goes. */
function menuGroups(sections: readonly ProjectSection[]): [ProjectSection, string][][] {
  return GROUPS.map((g) => g.filter(([s]) => sections.includes(s))).filter((g) => g.length);
}

/** A section's page under the project; the overview is the project's home. */
const pathOf = (s: ProjectSection) => (s === 'overview' ? '' : s);

/**
 * NSB's project menu (Jason, 2026-10-03: "the admin view of the project needs
 * a better menu than way at the bottom"): one row of words under the header on
 * every page of a project, its groups set apart by a rule, the current section
 * held by an ink rule under its words. On a narrow screen the row scrolls
 * sideways, the current section brought into view; it never wraps into a
 * block above the page (guide §20.11). The client has their own way round
 * (the links under the price on their home). On the project's home it sits
 * under the name and the place (`inline`), clear of the banner's paint.
 */
export function ProjectMenu({ projectId, pathname, inline = false }: { projectId: string; pathname: string; inline?: boolean }) {
  const { data: project } = useProject(projectId);
  const row = useRef<HTMLUListElement>(null);
  // Which ends have more past them: that end fades, so a row cut short says it goes on.
  const [more, setMore] = useState({ left: false, right: false });
  const base = `/p/${projectId}`;
  const under = (path: string) => pathname === `${base}/${path}` || pathname.startsWith(`${base}/${path}/`);
  // Settings holds the pages in it.
  const on = (path: string) => (!path ? pathname === base : path === 'settings' ? SETTINGS_PAGES.some(under) : under(path));
  useEffect(() => {
    const el = row.current;
    if (!el) return;
    el.querySelector('[aria-current="page"]')?.scrollIntoView({ block: 'nearest', inline: 'center' });
    const look = () => setMore({ left: el.scrollLeft > 2, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
    look();
    el.addEventListener('scroll', look, { passive: true });
    window.addEventListener('resize', look);
    return () => {
      el.removeEventListener('scroll', look);
      window.removeEventListener('resize', look);
    };
  }, [pathname, project]);
  const fade = `linear-gradient(to right, ${more.left ? 'transparent, black 2.5rem' : 'black'}, ${more.right ? 'black calc(100% - 2.5rem), transparent' : 'black'})`;
  // A client, or a view as one, has no use for NSB's sections.
  if (!project || project.viewerRole !== 'contractor') return null;
  return (
    <nav aria-label={`${project.name}: sections`} className={inline ? 'mt-7' : 'shell mt-2'}>
      <ul
        ref={row}
        className="flex items-stretch gap-x-5 overflow-x-auto overflow-y-hidden border-b border-rule [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ maskImage: fade, WebkitMaskImage: fade }}
      >
        {menuGroups(project.sections).map((group, g) => (
          <li key={g} className={`flex shrink-0 items-stretch gap-x-5 ${g ? 'border-l border-rule pl-5' : ''}`}>
            {group.map(([section, label]) => ({ path: pathOf(section), label })).map(({ path, label }) => (
              <Link
                key={path}
                to={path ? `${base}/${path}` : base}
                aria-current={on(path) ? 'page' : undefined}
                className={`label -mb-px flex min-h-11 shrink-0 items-center whitespace-nowrap border-b-2 transition-colors duration-[120ms] ${on(path) ? 'border-ink !text-ink' : 'border-transparent hover:!text-ink'}`}
              >
                {label}
              </Link>
            ))}
          </li>
        ))}
      </ul>
    </nav>
  );
}
